import { DocumentTextIcon, TrashIcon } from "@heroicons/react/24/outline";
import {
  ScriptAsset,
  SyncImportFailureCode,
  useCheckProcessSync,
  useConnectProcessRepo,
  useDeleteScriptAsset,
  useExportProcessRevision,
  useProcessPublishedRevisions,
  useSyncProcessRevisions,
  useProcessGitConnection,
  usePutScriptAsset,
  useScriptAssets,
} from "@via/api/script-assets.api.tsx";
import MonacoEditor from "@via/components/monaco-editor/monaco-editor";
import { useIntegrationTokens } from "@via/api/integration-tokens.api.tsx";
import { classNames } from "@via/utils/helpers.tsx";
import { ChangeEvent, JSX, useEffect, useMemo, useRef, useState } from "react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import {
  assetPathProblem,
  defaultExecutable,
  storedAssetPath,
} from "./script-asset-path.ts";

/**
 * A file picked for upload but not yet sent. Picking only stages it, so the author can place it
 * under a subfolder (tests/fixtures.yaml) rather than always getting bin/<name>.
 */
type PendingUpload = {
  path: string;
  contents: string;
  executable: boolean;
  /** Once the author sets the execute bit by hand, editing the path no longer changes it. */
  executableChosen: boolean;
};

/**
 * Files a process owns and exports into `bin/` of its own repository.
 *
 * This is deliberately separate from the Scripts tab. Scripts holds the process body, header and
 * footer, which are columns on the revision. These are whole files that ship alongside it, and
 * they are the only way a process can call an R or Python helper by name at run time.
 */

/** Only text can round trip: the API carries `contents` as a JSON string, not bytes. */
const TEXT_EXTENSIONS = [
  ".r",
  ".py",
  ".sh",
  ".bash",
  ".pl",
  ".rb",
  ".jl",
  ".txt",
  ".csv",
  ".tsv",
  ".json",
  ".yaml",
  ".yml",
  ".md",
];

const languageFor = (path: string): string => {
  const lower = path.toLowerCase();
  if (lower.endsWith(".r")) {
    return "r";
  }
  if (lower.endsWith(".py")) {
    return "python";
  }
  if (lower.endsWith(".sh") || lower.endsWith(".bash")) {
    return "shell";
  }
  if (lower.endsWith(".json")) {
    return "json";
  }
  if (lower.endsWith(".yaml") || lower.endsWith(".yml")) {
    return "yaml";
  }
  return "plaintext";
};

const looksLikeText = (name: string): boolean =>
  TEXT_EXTENSIONS.some(extension => name.toLowerCase().endsWith(extension));

/**
 * `unconfirmed`, `commit-moved` and `stale-consent` are all fixed by running the check again and
 * consenting fresh, so a sync failure carrying one of these codes is worth a retry with no other
 * action. `invalid` and `conflict` are not: the source repository or the underlying data conflict
 * has to change first. See `SyncImportFailureCode` in `script-assets.api.tsx` for the full
 * reasoning behind each code.
 */
const RETRYABLE_SYNC_FAILURE_CODES: SyncImportFailureCode[] = [
  "unconfirmed",
  "commit-moved",
  "stale-consent",
];

const isRetryableSyncFailure = (code: SyncImportFailureCode): boolean =>
  RETRYABLE_SYNC_FAILURE_CODES.includes(code);

export default function ScriptAssets({
  processId,
  canEditProcess,
}: {
  processId: number;
  canEditProcess: boolean;
}): JSX.Element {
  const { data: assets, isLoading } = useScriptAssets(processId);
  const putAsset = usePutScriptAsset(processId);
  const deleteAsset = useDeleteScriptAsset(processId);
  const connectRepo = useConnectProcessRepo(processId);
  const exportRevision = useExportProcessRevision(processId);
  const { data: published, isLoading: publishedLoading } =
    useProcessPublishedRevisions(processId);
  const checkSync = useCheckProcessSync(processId);
  const checkedPlan = checkSync.data;
  const syncRevisions = useSyncProcessRevisions(processId);
  const synced = syncRevisions.data;
  // Never preselected: this is the fail closed gate Spike C requires before anything importable
  // is stored, matching the `.vf` import wizard's own consent step. Reset on every fresh check
  // (see the Sync button below), so a tick from a previous plan can never carry over onto a new
  // one that names different commits.
  const [syncConsentChecked, setSyncConsentChecked] = useState(false);

  /**
   * Pressing Sync restores FIRST, then asks for the import preview. The order is the whole point.
   *
   * `POST /git/sync` with no body performs zero per-candidate git reads (`readProcessSync` reads
   * a candidate only when this call was actually asked to import it), so the restore, the safe
   * half a user presses this button for, lands after one `ls-remote` and a tag walk.
   * `POST /git/sync/check` is the expensive half: it is the only `fullDiscovery: true` caller, so
   * it reads, parses, hashes and divergence-checks every importable candidate the repository
   * offers. Checking first meant an HTTP timeout in that sweep left the restore unperformed,
   * which is exactly the failure the restore-must-not-be-held-hostage rule exists to prevent.
   *
   * The preview still runs after every press, and it runs against post-restore state, so a
   * revision this call just un-hid comes back as `alreadyPresent` rather than as something still
   * to restore. A failing or slow check now costs the preview only.
   */
  const onSyncFromRepository = async (): Promise<void> => {
    setSyncConsentChecked(false);
    // Any plan still on screen describes the state before this restore. Cleared before the write
    // rather than after it, so there is no window where a stale plan sits next to a fresh result.
    checkSync.reset();
    try {
      await syncRevisions.mutateAsync();
    } catch {
      // Already surfaced by the hook's own error notification. The preview below is still worth
      // asking for: it writes nothing, and it says what a retry would have to deal with.
    }
    checkSync.mutate();
  };

  // The exact commit id the check reported for each tag, never recomputed or re-derived: this is
  // what makes the confirm button's consent mean something, since the server re-verifies the tag
  // still points there and refuses if it moved. A candidate whose own validation failed
  // (`validatedCommitId` null) has no commit to bind consent to, so it is left out; it already has
  // its own entry in `checkedPlan.importFailures`.
  const syncExpectedCommitIds = useMemo(() => {
    const map: Record<string, string> = {};
    for (const entry of checkedPlan?.importable ?? []) {
      if (null !== entry.validatedCommitId) {
        map[entry.tag] = entry.validatedCommitId;
      }
    }
    return map;
  }, [checkedPlan]);

  const editorRef = useRef<unknown>(null);
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [draft, setDraft] = useState<string>("");
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingUpload | null>(null);

  const [repoUrl, setRepoUrl] = useState<string>("");
  const [provider, setProvider] = useState<string>("github");
  const [defaultBranch, setDefaultBranch] = useState<string>("main");
  // `null` means undecided: nobody has chosen yet, and the defaulting effect below is free to
  // fill it in. `""` means the user explicitly chose no credential, a real decision that must
  // stick. A digit string means that credential. Collapsing "undecided" and "explicitly none"
  // into one empty string value was the bug: there was no way to tell "please pick one" from
  // "I picked public repository," so the defaulting effect could not help but keep overwriting
  // the second with the first.
  const [serviceTokenId, setServiceTokenId] = useState<string | null>(null);

  const {
    data: connection,
    isLoading: isConnectionLoading,
    isError: isConnectionError,
  } = useProcessGitConnection(processId);
  const isConnected = undefined !== connection && null !== connection;

  // 100 rather than the default 10: this is a picker, and a user whose eleventh credential is the
  // right one would otherwise see a list that silently omits it.
  const { data: credentialPage } = useIntegrationTokens(0, 100);

  // `ServiceTokenType` already uses the same "github" / "gitlab" / "bitbucket" strings as the
  // provider enum, so the two selects stay consistent by construction rather than by a mapping
  // table that could drift.
  const matchingCredentials = useMemo(
    () =>
      (credentialPage?.data ?? [])
        .filter(credential => credential.tokenType === provider)
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        ),
    [credentialPage, provider]
  );

  // When the process was connected by someone else, or the credential is simply past the caller's
  // own page cap, `useIntegrationTokens` will not carry it, and the select would otherwise fall
  // back to "No credential," which is a second copy of the exact bug this panel exists to fix: a
  // value the panel shows as true when it is not. This surfaces the stored id honestly, by id
  // only. No credential name, scope or secret material is fetched or rendered here, because the id
  // is all the caller is entitled to see for a credential that is not theirs.
  const missingCredentialId = useMemo(() => {
    if (undefined === connection || null === connection) {
      return null;
    }
    if (null === connection.serviceTokenId) {
      return null;
    }
    const known = matchingCredentials.some(
      credential => credential.id === connection.serviceTokenId
    );
    return known ? null : connection.serviceTokenId;
  }, [connection, matchingCredentials]);

  // Defaulting, not binding. It only ever fills an undecided (`null`) box, and only while a
  // provider change or the credential list arriving might have just produced a candidate to fill
  // it with. Once `serviceTokenId` holds a decision, `""` (explicitly no credential) included,
  // this effect leaves it alone forever, so there is no way for it to fight a choice the user
  // already made. The provider select's `onChange` is what resets the box back to `null` on a
  // provider change, which is what makes "changing the provider re-defaults" happen.
  //
  // Declared before the seeding effect below on purpose. Both effects can run in the same commit,
  // when the connection and the credential list are already resolved on first render (as they are
  // in a test with both mocked synchronously). Passive effects for one commit run in declaration
  // order against the closure state captured at that render, so if this effect ran second it would
  // read the pre seed value of `serviceTokenId` and overwrite the seeded credential with the
  // default before the seed ever became visible. Declared first, this effect fires and sets the
  // default, then the seeding effect fires and overwrites it with the stored value, which wins the
  // batch. The next render re-runs this effect with the now-seeded id, which is no longer `null`,
  // and it is a no-op from then on.
  useEffect(() => {
    if (null !== serviceTokenId || 0 === matchingCredentials.length) {
      return;
    }
    setServiceTokenId(String(matchingCredentials[0].id));
  }, [matchingCredentials, serviceTokenId]);

  // Seed once, not on every change of `connection`. React Query refetches on window focus and
  // after any invalidation, so an unguarded effect would overwrite whatever the user had typed
  // into the branch or credential field the moment the tab regained focus. The ref is what makes
  // this a one time seed rather than a binding.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || undefined === connection || null === connection) {
      return;
    }
    seeded.current = true;
    setRepoUrl(connection.repoUrl);
    setProvider(connection.provider);
    setDefaultBranch(connection.defaultBranch);
    setServiceTokenId(
      null === connection.serviceTokenId
        ? ""
        : String(connection.serviceTokenId)
    );
  }, [connection]);

  const selected = useMemo(
    () => (assets ?? []).find(asset => asset.path === selectedPath) ?? null,
    [assets, selectedPath]
  );

  const openAsset = (asset: ScriptAsset): void => {
    setSelectedPath(asset.path);
    setDraft(asset.contents);
  };

  const onUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (undefined === file) {
      return;
    }
    if (!looksLikeText(file.name)) {
      setUploadError(
        `${file.name} does not look like a text file. A process can only carry text scripts, because the file is stored and exported verbatim as text.`
      );
      return;
    }
    setUploadError(null);

    const contents = await file.text();
    setPending({
      path: file.name,
      contents,
      executable: defaultExecutable(file.name),
      executableChosen: false,
    });
  };

  const onPendingPath = (path: string): void => {
    setPending(current =>
      null === current
        ? null
        : {
            ...current,
            path,
            executable: current.executableChosen
              ? current.executable
              : defaultExecutable(path),
          }
    );
  };

  const pendingProblem =
    null === pending ? null : assetPathProblem(pending.path);

  const onAddPending = async () => {
    if (null === pending || null !== pendingProblem) {
      return;
    }
    // A bare filename is stored under bin/, which is the only directory Nextflow puts on PATH.
    // The server decides the final path, so the list shows what it returned, not what was sent.
    let saved: ScriptAsset;
    try {
      saved = await putAsset.mutateAsync({
        path: pending.path,
        contents: pending.contents,
        executable: pending.executable,
      });
    } catch {
      // The mutation's notification carries the server's reason (a reserved name, a frozen
      // revision). The row stays open so the path can be corrected without picking the file again.
      return;
    }
    setPending(null);
    openAsset(saved);
  };

  const onSave = async () => {
    if (null === selected) {
      return;
    }
    const saved = await putAsset.mutateAsync({
      path: selected.path,
      contents: draft,
      executable: selected.executable,
    });
    openAsset(saved);
  };

  const onDelete = async (path: string) => {
    await deleteAsset.mutateAsync({ path });
    if (path === selectedPath) {
      setSelectedPath(null);
      setDraft("");
    }
  };

  const exported = exportRevision.data;

  return (
    <div className="flex h-full flex-col gap-4 p-3">
      <PanelGroup direction="horizontal" className="min-h-96 flex-1">
        <Panel defaultSize={28} minSize={18}>
          <div className="flex flex-col gap-3 pr-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">Files</h3>
              {canEditProcess ? (
                <label className="cursor-pointer text-sm font-medium text-indigo-600 hover:text-indigo-500">
                  Upload
                  <input
                    type="file"
                    className="hidden"
                    onChange={onUpload}
                    aria-label="Upload a script file"
                  />
                </label>
              ) : null}
            </div>

            {null !== uploadError ? (
              <p className="text-sm text-red-600">{uploadError}</p>
            ) : null}

            {null !== pending ? (
              <div className="flex flex-col gap-2 rounded-md border border-gray-200 p-2">
                <label className="flex flex-col gap-1 text-xs font-medium text-gray-700">
                  Path in the process repository
                  <input
                    type="text"
                    value={pending.path}
                    onChange={event => onPendingPath(event.target.value)}
                    aria-invalid={null !== pendingProblem}
                    className="rounded border border-gray-300 px-2 py-1 font-mono text-sm font-normal text-gray-900"
                  />
                </label>
                {null !== pendingProblem ? (
                  <p role="alert" className="text-xs text-red-600">
                    {pendingProblem}
                  </p>
                ) : pending.path.includes("/") ? null : (
                  <p className="text-xs text-gray-500">
                    Stored as <code>{storedAssetPath(pending.path)}</code>. Add
                    a folder, such as <code>tests/</code>, to put it elsewhere.
                  </p>
                )}
                <label className="flex items-center gap-2 text-xs text-gray-700">
                  <input
                    type="checkbox"
                    checked={pending.executable}
                    onChange={event =>
                      setPending({
                        ...pending,
                        executable: event.target.checked,
                        executableChosen: true,
                      })
                    }
                  />
                  Executable
                </label>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setPending(null)}
                    className="rounded px-3 py-1 text-sm text-gray-700 hover:bg-gray-100">
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={onAddPending}
                    disabled={putAsset.isPending || null !== pendingProblem}
                    className="rounded bg-indigo-600 px-3 py-1 text-sm text-white disabled:bg-gray-300">
                    Add file
                  </button>
                </div>
              </div>
            ) : null}

            {isLoading ? (
              <p className="text-sm text-gray-500">Loading files...</p>
            ) : null}

            {!isLoading && 0 === (assets ?? []).length ? (
              <p className="text-sm text-gray-500">
                No files yet. Upload an R, Python or shell script and it exports
                into <code>bin/</code>, executable, so the process can call it
                by name. Give it a folder, such as{" "}
                <code>tests/fixtures.yaml</code>, to put it elsewhere.
              </p>
            ) : null}

            <ul role="list" className="flex flex-col space-y-1.5">
              {(assets ?? []).map(asset => (
                <li
                  key={asset.path}
                  className={classNames(
                    asset.path === selectedPath
                      ? "font-medium text-indigo-600"
                      : "text-black hover:text-indigo-600",
                    "group flex flex-row items-center justify-between gap-x-2 rounded-md text-[15px] leading-6"
                  )}>
                  <button
                    type="button"
                    onClick={() => openAsset(asset)}
                    className="flex flex-1 cursor-pointer flex-row items-center gap-x-2 text-left">
                    <DocumentTextIcon className="h-4 w-4 shrink-0 text-gray-400" />
                    <span className="truncate">{asset.path}</span>
                    {asset.executable ? (
                      <span className="shrink-0 rounded bg-green-50 px-1.5 py-0.5 text-[11px] text-green-700">
                        755
                      </span>
                    ) : null}
                  </button>
                  {canEditProcess ? (
                    <button
                      type="button"
                      aria-label={`Remove ${asset.path}`}
                      onClick={() => onDelete(asset.path)}
                      className="shrink-0 text-gray-400 hover:text-red-600">
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        </Panel>

        <PanelResizeHandle className="w-px bg-gray-200" />

        <Panel minSize={40}>
          {null === selected ? (
            <div className="flex h-full items-center justify-center text-sm text-gray-500">
              Select a file to view or edit it.
            </div>
          ) : (
            <div className="flex h-full flex-col gap-2 pl-3">
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm text-gray-700">
                  {selected.path}
                </span>
                {canEditProcess ? (
                  <button
                    type="button"
                    onClick={onSave}
                    disabled={putAsset.isPending || draft === selected.contents}
                    className="rounded bg-indigo-600 px-3 py-1 text-sm text-white disabled:bg-gray-300">
                    {putAsset.isPending ? "Saving..." : "Save"}
                  </button>
                ) : null}
              </div>
              <MonacoEditor
                editorRef={editorRef}
                language={languageFor(selected.path)}
                value={draft}
                onChange={
                  canEditProcess
                    ? (value?: string) => setDraft(value ?? "")
                    : undefined
                }
                options={{
                  readOnly: !canEditProcess,
                  minimap: { enabled: false },
                }}
              />
            </div>
          )}
        </Panel>
      </PanelGroup>

      <div className="rounded-md border border-gray-200 p-3">
        <h3 className="text-sm font-semibold text-gray-900">Git repository</h3>

        {isConnectionLoading ? (
          // A short placeholder, not the form: while this request is in flight, `isConnected` has
          // nothing to go on yet, and rendering the unconnected form here is what let a connected
          // process flash the editable "Repository URL" input and a "Connect" button the moment
          // before the real, connected data arrived.
          <p className="mt-1 text-sm text-gray-500">
            Loading the repository connection...
          </p>
        ) : isConnectionError ? (
          // The loading case self corrects once the request settles. A failed request never does:
          // `connection` stays `undefined` forever, so without this branch the form below renders
          // as if the process were unconnected, and submitting it is the exact refusal this panel
          // was built to stop.
          <p className="mt-1 text-sm text-red-600">
            The repository connection could not be read. Reload the page and try
            again.
          </p>
        ) : (
          <>
            <p className="mt-1 text-sm text-gray-500">
              {isConnected
                ? "The repository is fixed once set, because published revision tags live in it. The branch and the credential can still be changed."
                : "Export this revision to its own repository. The repository must be https, not ssh."}
            </p>

            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-4">
              {isConnected ? (
                <div className="sm:col-span-2">
                  <p className="text-xs text-gray-500">Connected to</p>
                  <p className="font-mono text-sm break-all text-gray-700">
                    {connection.repoUrl}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    publishes to{" "}
                    <span className="font-mono">
                      processes/{connection.repoPath}/
                    </span>
                  </p>
                </div>
              ) : (
                <input
                  className="rounded border border-gray-300 px-2 py-1 text-sm sm:col-span-2"
                  placeholder="https://github.com/owner/repo.git"
                  value={repoUrl}
                  aria-label="Repository URL"
                  onChange={event => setRepoUrl(event.target.value)}
                />
              )}
              {isConnected ? (
                // The provider is read straight off the frozen URL, so it is as create-only in
                // practice as the URL already is. Leaving it editable is what let changing github
                // to gitlab on a connected process silently reset and re-default the stored push
                // credential to one belonging to a different host, while the panel kept showing
                // the original github URL as fixed text right next to it.
                <div>
                  <p className="text-xs text-gray-500">Provider</p>
                  <p className="text-sm text-gray-700">{connection.provider}</p>
                </div>
              ) : (
                <select
                  className="rounded border border-gray-300 px-2 py-1 text-sm"
                  value={provider}
                  aria-label="Provider"
                  onChange={event => {
                    // Changing the provider invalidates whatever credential was chosen for the old
                    // one, stale or not. Resetting to `null` (undecided) rather than leaving the
                    // old id in place is what stops a provider A credential id from ever being
                    // submitted with a provider B connection: the defaulting effect then either
                    // fills in the new provider's own default or, if it has no matching
                    // credentials, leaves the box empty and disabled.
                    setProvider(event.target.value);
                    setServiceTokenId(null);
                  }}>
                  <option value="github">github</option>
                  <option value="gitlab">gitlab</option>
                  <option value="bitbucket">bitbucket</option>
                </select>
              )}
              <input
                className="rounded border border-gray-300 px-2 py-1 text-sm"
                placeholder="main"
                value={defaultBranch}
                aria-label="Default branch"
                onChange={event => setDefaultBranch(event.target.value)}
              />
              <div>
                <select
                  className="w-full rounded border border-gray-300 px-2 py-1 text-sm disabled:bg-gray-100"
                  value={serviceTokenId ?? ""}
                  aria-label="Credential"
                  disabled={
                    0 === matchingCredentials.length &&
                    null === missingCredentialId
                  }
                  onChange={event => setServiceTokenId(event.target.value)}>
                  <option value="">No credential (public repository)</option>
                  {matchingCredentials.map(credential => (
                    <option key={credential.id} value={credential.id}>
                      {credential.name}
                    </option>
                  ))}
                  {null !== missingCredentialId ? (
                    // The stored id is real, it just is not one `useIntegrationTokens` returned for
                    // this caller. Naming it by id, and nothing else, is what lets the select show
                    // the true selected value instead of silently falling back to "No credential."
                    <option value={missingCredentialId}>
                      {`Credential ${missingCredentialId} (not one of yours)`}
                    </option>
                  ) : null}
                </select>
                {0 === matchingCredentials.length &&
                null === missingCredentialId ? (
                  <p className="mt-1 text-xs text-gray-500">
                    Add a {provider} credential on the integrations page to push
                    to a private repository.
                  </p>
                ) : null}
              </div>
            </div>

            <div className="mt-3 flex gap-2">
              <button
                type="button"
                disabled={!canEditProcess || connectRepo.isPending}
                onClick={() =>
                  connectRepo.mutate({
                    repoUrl,
                    provider,
                    defaultBranch,
                    // Both `null` (undecided) and `""` (explicitly no credential) mean "send no
                    // credential." Only a digit string names an actual credential to submit.
                    serviceTokenId:
                      null === serviceTokenId || "" === serviceTokenId
                        ? null
                        : Number(serviceTokenId),
                  })
                }
                className="rounded border border-gray-300 px-3 py-1 text-sm disabled:text-gray-400">
                {connectRepo.isPending
                  ? "Saving..."
                  : isConnected
                    ? "Update connection"
                    : "Connect"}
              </button>
              <button
                type="button"
                disabled={!canEditProcess || exportRevision.isPending}
                onClick={() => exportRevision.mutate()}
                className="rounded bg-indigo-600 px-3 py-1 text-sm text-white disabled:bg-gray-300">
                {exportRevision.isPending ? "Exporting..." : "Export"}
              </button>
            </div>

            {undefined !== exported ? (
              <div className="mt-3 rounded bg-gray-50 p-3 text-sm">
                {exported.committed ? (
                  <p className="font-medium text-green-700">
                    Committed revision {exported.revId} to {exported.branch}
                    {undefined !== exported.tag ? ` as ${exported.tag}` : ""}.
                  </p>
                ) : (
                  <p className="font-medium text-gray-700">
                    Nothing to commit. The repository already matches this
                    revision, so no second commit was made.
                  </p>
                )}
                <p className="mt-1 font-mono text-xs break-all text-gray-600">
                  {exported.contentHash}
                </p>
                {0 < (exported.files ?? []).length ? (
                  <ul className="mt-2 list-inside list-disc font-mono text-xs text-gray-600">
                    {exported.files.map(file => (
                      <li key={file}>{file}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}

            {/*
              What the REPOSITORY holds, which is not the same as what this editor lists.
              Deleting a revision here is a soft delete and never retracts its tag, so the
              repository can sit permanently ahead of every revision still held. Export refuses
              anything below the repository's highest revision, and before this panel existed that
              refusal named a revision the user could not see anywhere in the product.
            */}
            {publishedLoading ? (
              <p className="mt-4 text-sm text-gray-500">
                Reading what the repository holds...
              </p>
            ) : undefined !== published && null !== published ? (
              <div className="mt-4 border-t border-gray-200 pt-3">
                <h4 className="text-sm font-medium text-gray-900">
                  Published in this repository
                </h4>

                {0 === published.revisions.length ? (
                  <p className="mt-1 text-sm text-gray-600">
                    Nothing has been published from this process yet.
                  </p>
                ) : (
                  <>
                    <ul className="mt-2 space-y-1">
                      {published.revisions.map(revision => (
                        <li
                          key={revision.tag}
                          className="flex items-center justify-between gap-3 text-sm">
                          <span className="font-mono text-xs break-all text-gray-700">
                            {revision.tag}
                          </span>
                          {revision.presentLocally ? (
                            <span className="shrink-0 text-xs text-gray-500">
                              revision {revision.revId}
                            </span>
                          ) : (
                            <span className="shrink-0 text-xs font-medium text-amber-700">
                              not in this process
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>

                    <div className="mt-2 flex items-center gap-2">
                      <button
                        type="button"
                        disabled={
                          !canEditProcess ||
                          checkSync.isPending ||
                          syncRevisions.isPending
                        }
                        onClick={() => {
                          void onSyncFromRepository();
                        }}
                        className="rounded border border-gray-300 px-3 py-1 text-sm disabled:text-gray-400">
                        {syncRevisions.isPending
                          ? "Syncing..."
                          : checkSync.isPending
                            ? "Checking..."
                            : "Sync from repository"}
                      </button>
                      <span className="text-xs text-gray-500">
                        Restores revisions this process hid that the repository
                        still publishes. Never deletes anything from the
                        repository.
                      </span>
                    </div>

                    {/*
                      The plan a check just read back: what will be restored outright, what is
                      importable and at which commit, and what neither this installation nor the
                      import path can resolve. Rendered before anything is written, so a person
                      sees exactly this before the consent step below (if any) is even shown.
                    */}
                    {undefined !== checkedPlan ? (
                      <div className="mt-2 rounded border border-gray-200 p-2 text-sm">
                        {0 < checkedPlan.restore.length ? (
                          <p className="text-gray-700">
                            Will restore {checkedPlan.restore.length} revision
                            {1 === checkedPlan.restore.length ? "" : "s"}:{" "}
                            {checkedPlan.restore
                              .map(entry => entry.tag)
                              .join(", ")}
                            .
                          </p>
                        ) : null}

                        {0 < checkedPlan.importable.length ? (
                          <div className="mt-1">
                            <p className="font-medium text-gray-800">
                              Importable from the repository:
                            </p>
                            <ul className="mt-1 list-inside list-disc text-xs text-gray-700">
                              {checkedPlan.importable.map(entry => (
                                <li
                                  key={entry.tag}
                                  data-testid={`syncImportable-${entry.tag}`}>
                                  {entry.tag} (revision {entry.revId}):{" "}
                                  {null !== entry.validatedCommitId
                                    ? `commit ${entry.validatedCommitId}`
                                    : "could not be validated, see below"}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : null}

                        {0 < checkedPlan.unresolved.length ? (
                          <ul className="mt-1 list-inside list-disc text-xs text-gray-600">
                            {checkedPlan.unresolved.map(entry => (
                              <li key={entry.tag}>
                                {entry.tag}: {entry.reason}
                              </li>
                            ))}
                          </ul>
                        ) : null}

                        {/*
                          Spike C's consent step. Only rendered when consentText is non null,
                          which is exactly the case where something validated cleanly and would
                          store untrusted code. A pure restore, or a plan where nothing survived
                          validation, needs none of this, and needs no second write either: the
                          restore this plan describes already ran, before the check that produced
                          the plan. See onSyncFromRepository above.
                        */}
                        {null !== checkedPlan.consentText ? (
                          <div className="mt-3 space-y-2 border-t border-gray-200 pt-2">
                            <div className="rounded-md border border-gray-200 bg-gray-50 p-2 text-xs whitespace-pre-wrap text-gray-800">
                              {checkedPlan.consentText}
                            </div>
                            <label className="flex items-start gap-2 text-xs text-gray-900">
                              <input
                                type="checkbox"
                                className="mt-0.5"
                                data-testid="syncConsentCheckbox"
                                checked={syncConsentChecked}
                                onChange={event =>
                                  setSyncConsentChecked(event.target.checked)
                                }
                              />
                              <span>
                                I have read the source and understand this code
                                will be stored but not executed.
                              </span>
                            </label>
                            <button
                              type="button"
                              data-testid="syncConfirmImport"
                              disabled={
                                !canEditProcess ||
                                !syncConsentChecked ||
                                syncRevisions.isPending
                              }
                              onClick={() =>
                                syncRevisions.mutate(
                                  {
                                    confirmImport: true,
                                    expectedCommitIds: syncExpectedCommitIds,
                                  },
                                  {
                                    // Once the import applies, the plan that produced this
                                    // consent is spent: whatever was importable either landed
                                    // (see `synced.imported`) or failed (see
                                    // `synced.importFailures`). Clearing it here is what makes
                                    // the consent step disappear once acted on, rather than
                                    // leaving a ticked checkbox and an enabled button next to a
                                    // result that already happened.
                                    onSuccess: () => checkSync.reset(),
                                  }
                                )
                              }
                              className="rounded bg-indigo-600 px-3 py-1 text-xs text-white disabled:bg-gray-300">
                              {syncRevisions.isPending
                                ? "Syncing..."
                                : "Confirm and sync"}
                            </button>
                          </div>
                        ) : null}
                      </div>
                    ) : null}

                    {undefined !== synced ? (
                      <div className="mt-2 rounded bg-gray-50 p-2 text-sm">
                        <p className="font-medium text-gray-800">
                          {0 === synced.restored && 0 === synced.imported
                            ? "Nothing to restore. Every revision the repository publishes is already visible here, or was published elsewhere."
                            : [
                                0 < synced.restored
                                  ? `Restored ${synced.restored} revision${1 === synced.restored ? "" : "s"}.`
                                  : null,
                                0 < synced.imported
                                  ? `Imported ${synced.imported} revision${1 === synced.imported ? "" : "s"}.`
                                  : null,
                              ]
                                .filter((line): line is string => null !== line)
                                .join(" ")}
                        </p>
                        {0 < synced.unresolved.length ? (
                          <ul className="mt-1 list-inside list-disc text-xs text-gray-600">
                            {synced.unresolved.map(entry => (
                              <li key={entry.tag}>
                                {entry.tag}: {entry.reason}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                        {/*
                          Every importable revision this call did not import, distinguished by
                          code rather than shown as identical prose: unconfirmed, commit-moved and
                          stale-consent are all fixed by checking again, while invalid and conflict
                          need the source repository or the underlying data conflict to change
                          first. See isRetryableSyncFailure above.
                        */}
                        {0 < synced.importFailures.length ? (
                          <ul className="mt-1 list-inside list-disc text-xs text-gray-600">
                            {synced.importFailures.map(failure => (
                              <li
                                key={`${failure.tag}-${failure.code}`}
                                data-testid={`syncFailure-${failure.tag}`}>
                                <span
                                  className={
                                    isRetryableSyncFailure(failure.code)
                                      ? "font-medium text-amber-700"
                                      : "font-medium text-red-700"
                                  }>
                                  {isRetryableSyncFailure(failure.code)
                                    ? "Check again"
                                    : "Conflict"}
                                </span>{" "}
                                {failure.tag}: {failure.reason}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                    ) : null}

                    {published.repositoryIsAhead ? (
                      <p className="mt-2 rounded bg-amber-50 p-2 text-sm text-amber-800">
                        This repository has revision {published.highestExported}
                        , which is newer than anything this process still holds
                        {null === published.highestLocal
                          ? ""
                          : ` (revision ${published.highestLocal})`}
                        . Export will refuse until you save a new revision. The
                        older tags stay in the repository, because deleting a
                        revision here does not remove what was already
                        published.
                      </p>
                    ) : null}
                  </>
                )}
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
